/** Strip non-digits from a CNPJ input (masked or raw). */
export function normalizeCnpj(value: string): string {
  return value.replace(/\D/g, "");
}

function allDigitsSame(digits: string): boolean {
  return /^(\d)\1+$/.test(digits);
}

function checkDigit(base: string, weights: number[]): number {
  let sum = 0;
  for (let i = 0; i < weights.length; i++) {
    sum += Number(base[i]) * weights[i]!;
  }
  const rem = sum % 11;
  return rem < 2 ? 0 : 11 - rem;
}

/**
 * Validate a Brazilian CNPJ (with or without punctuation).
 * Rejects wrong length, all-same-digit sequences, and invalid check digits.
 */
export function isValidCnpj(value: string): boolean {
  const digits = normalizeCnpj(value);
  if (digits.length !== 14) return false;
  if (allDigitsSame(digits)) return false;

  const firstWeights = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const secondWeights = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  const d13 = checkDigit(digits.slice(0, 12), firstWeights);
  if (d13 !== Number(digits[12])) return false;

  const d14 = checkDigit(digits.slice(0, 13), secondWeights);
  return d14 === Number(digits[13]);
}

/** Format digits as `00.000.000/0000-00`. Returns empty string if no digits. */
export function formatCnpj(value: string): string {
  const digits = normalizeCnpj(value).slice(0, 14);
  if (digits.length === 0) return "";

  const p1 = digits.slice(0, 2);
  const p2 = digits.slice(2, 5);
  const p3 = digits.slice(5, 8);
  const p4 = digits.slice(8, 12);
  const p5 = digits.slice(12, 14);

  let out = p1;
  if (p2) out += `.${p2}`;
  if (p3) out += `.${p3}`;
  if (p4) out += `/${p4}`;
  if (p5) out += `-${p5}`;
  return out;
}

/** Apply mask from raw input (typed or pasted). */
export function maskCnpjInput(raw: string): string {
  return formatCnpj(raw);
}
